import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { LEGAL } from "@/lib/legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.status === "already_subscribed" ? "You're already on the list." : "Subscribed. Read Chapter Zero in your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer data-testid={FOOTER.section} className="relative bg-[#FAF7F2] border-t border-[#E7DFD5]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-y-12 gap-x-8">
          <div className="col-span-12 md:col-span-6">
            <div className="section-tag mb-6">§ Chapter Zero</div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.9] tracking-tight mb-8 max-w-xl">
              Get the first module <span className="italic text-[#B45309]">free</span>.
            </h3>
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
              <input
                type="email"
                required
                data-testid={FOOTER.newsletter}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@work.email"
                className="flex-1 bg-[#FFFFFF] border border-[#E7DFD5] px-4 py-4 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300"
              />
              <button data-testid={FOOTER.subscribe} disabled={loading} className="btn-ember text-sm">
                {loading ? "…" : "Send it"}
                <ArrowUpRight size={16} />
              </button>
            </form>
          </div>

          <div className="col-span-6 md:col-span-2 md:col-start-7">
            <div className="section-tag mb-6">Explore</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/manifesto" className="tacc-link">Manifesto</Link></li>
              <li><Link to="/press" className="tacc-link" data-testid="footer-press">Press Kit</Link></li>
              <li><Link to="/modules" className="tacc-link">8 Modules</Link></li>
              <li><Link to="/books" className="tacc-link">Books · Kindle</Link></li>
              <li><Link to="/b2b" className="tacc-link">For Companies</Link></li>
              <li><Link to="/for/healthcare" className="tacc-link">For Healthcare</Link></li>
              <li><Link to="/for/first-responders" className="tacc-link">For First Responders</Link></li>
              <li><Link to="/for/hospitality" className="tacc-link">For Hospitality</Link></li>
              <li><Link to="/for/call-centers" className="tacc-link">For Call Centers</Link></li>
            </ul>
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="section-tag mb-6">Buy</div>
            <ul className="space-y-3 text-sm">
              <li><a href="https://richculm.gumroad.com/l/lkejj" target="_blank" rel="noreferrer" className="tacc-link">8-Module Bundle</a></li>
              <li><a href="https://www.amazon.com/dp/B0G5286848" target="_blank" rel="noreferrer" className="tacc-link">Healing Manual (Kindle)</a></li>
              <li><a href="https://www.amazon.com/dp/B0H5D49156" target="_blank" rel="noreferrer" className="tacc-link">Work Book (Kindle)</a></li>
              <li><Link to="/b2b#quote" className="tacc-link">Enterprise Quote</Link></li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-2">
            <div className="section-tag mb-6">Community</div>
            <ul className="space-y-3 text-sm">
              <li><a href="https://discord.gg/5BhxH73ay" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-discord">Discord</a></li>
              <li><a href="https://www.youtube.com/@TACC-1107" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-youtube">YouTube</a></li>
              <li><a href="https://kick.com/tacc1107" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-kick">Kick</a></li>
              <li><a href="https://www.twitch.tv/tacc1107" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-twitch">Twitch</a></li>
              <li><a href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-facebook">Facebook</a></li>
              <li><a href="https://www.tiktok.com/@richculmer" target="_blank" rel="noreferrer" className="tacc-link" data-testid="social-tiktok">TikTok</a></li>
            </ul>
          </div>
        </div>

        <div className="tacc-rule my-16" />

        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="font-display text-[16vw] md:text-[9vw] leading-[0.85] tracking-tighter text-[#44403C]">
              T.A.C.C.<span className="text-[#B45309]">.</span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-col gap-2 text-xs font-mono text-[#78716C] md:text-right">
            <span data-testid="footer-copyright">© {new Date().getFullYear()} {LEGAL.entity} · Through Adversity Comes Change.</span>
            <span>All rights honored. Some rights reserved.</span>
            <span className="flex gap-4 md:justify-end" data-testid="footer-legal-links">
              <Link to="/terms" className="tacc-link" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="tacc-link" data-testid="footer-privacy">Privacy</Link>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
